<template>
  <dialog ref="dialogRef" class="modal">
    <div class="modal-box w-full max-w-2xl max-h-[90vh] overflow-y-auto">
      <div class="flex items-center gap-1 mb-4">
        <h3 class="font-bold text-lg">Détails de l'arbitrage BU</h3>
        <HelpButton />
      </div>

      <div v-if="arbitration" class="space-y-4">
        <!-- Info générales -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="text-sm font-semibold">Demandeur</label>
            <p class="text-base-content/80">
              {{ arbitration.requester?.firstName }} {{ arbitration.requester?.lastName }}
              <span v-if="arbitration.requester?.bu" class="badge badge-ghost badge-sm ml-1">{{ arbitration.requester.bu }}</span>
            </p>
          </div>
          <div>
            <label class="text-sm font-semibold">Société</label>
            <p class="text-base-content/80">{{ arbitration.company?.name || '-' }}</p>
          </div>
          <div>
            <label class="text-sm font-semibold">Effectif (snapshot)</label>
            <p class="text-base-content/80">{{ arbitration.companyWorkforce ?? 'inconnu' }} salarié(s)</p>
          </div>
          <div>
            <label class="text-sm font-semibold">BU demandée</label>
            <p class="text-base-content/80">{{ arbitration.requesterBu }}</p>
          </div>
          <div>
            <label class="text-sm font-semibold">Commercial assigné actuel</label>
            <p class="text-base-content/80">
              <template v-if="arbitration.company?.assignedCommercial">
                {{ arbitration.company.assignedCommercial.firstName }} {{ arbitration.company.assignedCommercial.lastName }}
                <span v-if="arbitration.company.assignedCommercial.bu" class="badge badge-ghost badge-sm ml-1">{{ arbitration.company.assignedCommercial.bu }}</span>
              </template>
              <template v-else>Aucun</template>
            </p>
          </div>
          <div>
            <label class="text-sm font-semibold">Devis lié</label>
            <p class="text-base-content/80">{{ arbitration.triggeringOrder?.quote?.quoteReference || '-' }}</p>
          </div>
          <div>
            <label class="text-sm font-semibold">Statut</label>
            <div class="mt-1">
              <span
                :class="{
                  'badge bg-blue-100': arbitration.status === 'PENDING',
                  'badge bg-emerald-100': arbitration.status === 'APPROVED',
                  'badge bg-red-100': arbitration.status === 'REJECTED',
                }"
              >
                {{ ARBITRATION_STATUS_LABELS[arbitration.status as keyof typeof ARBITRATION_STATUS_LABELS] || arbitration.status }}
              </span>
            </div>
          </div>
          <div>
            <label class="text-sm font-semibold">Date création</label>
            <p class="text-base-content/80">{{ formatDate(arbitration.createdAt) }}</p>
          </div>
        </div>

        <!-- Description/Raison -->
        <div class="divider my-2"></div>
        <div class="space-y-3">
          <div>
            <label class="text-sm font-semibold block mb-2">Justification du demandeur</label>
            <div class="p-3 bg-base-200 rounded border-l-4 border-primary">
              <template v-if="reasonObject">
                <div v-if="reasonObject.description" class="mb-3">
                  <p class="font-semibold">Description</p>
                  <p class="text-base-content/80">{{ reasonObject.description }}</p>
                </div>
                <div v-if="reasonObject.commercialArgument">
                  <p class="font-semibold">Argument commercial</p>
                  <p class="text-base-content/80 whitespace-pre-wrap">{{ reasonObject.commercialArgument }}</p>
                </div>
              </template>
              <p v-else class="text-base-content/80">Aucune justification spécifiée</p>
            </div>
          </div>
        </div>

        <!-- Effet d'approbation -->
        <div v-if="arbitration.status === 'PENDING'" class="alert alert-info">
          <p class="text-sm">
            <strong>Si approuvé :</strong> la société sera assignée au demandeur, et tout commercial de la BU
            <strong>{{ arbitration.requesterBu }}</strong> pourra ensuite y vendre sans nouvel arbitrage.<br />
            <strong>Si refusé :</strong> la commande liée est annulée automatiquement.
          </p>
        </div>

        <!-- Commentaires -->
        <div class="divider my-2"></div>
        <div class="space-y-3">
          <label class="text-sm font-semibold block">Commentaires</label>
          <div
            ref="commentBoxRef"
            v-if="localComment"
            class="p-3 pr-4 bg-base-200 rounded whitespace-pre-wrap text-sm text-base-content/80 max-h-64 overflow-y-auto scrollbar-thin"
          >
            {{ localComment }}
          </div>
          <p v-else class="text-sm text-base-content/50 italic">Aucun commentaire.</p>
          <div class="mt-2">
            <textarea
              v-model="newComment"
              class="textarea textarea-bordered w-full"
              rows="3"
              placeholder="Ajouter un commentaire..."
            ></textarea>
            <div class="flex justify-end mt-1">
              <button
                class="btn btn-sm btn-primary"
                :disabled="!newComment.trim() || isAddingComment"
                @click="handleAddComment"
              >
                <span v-if="isAddingComment" class="loading loading-spinner loading-xs"></span>
                Ajouter
              </button>
            </div>
          </div>
        </div>

        <!-- Section validation directeur (PENDING + ARBITRATION_VALIDATE) -->
        <template v-if="canValidate && arbitration.status === 'PENDING'">
          <div class="divider my-2"></div>
          <div class="space-y-3">
            <label class="text-sm font-semibold">Commentaire de décision (obligatoire en cas de rejet)</label>
            <textarea
              v-model="decisionComment"
              class="textarea textarea-bordered w-full"
              placeholder="Entrez votre commentaire..."
              rows="3"
            ></textarea>

            <div class="flex gap-2 justify-end pt-2">
              <button
                class="btn btn-error btn-sm"
                @click="handleReject"
                :disabled="!decisionComment.trim() || isProcessing"
              >
                <span v-if="isProcessing" class="loading loading-spinner loading-xs"></span>
                Refuser (annule la commande)
              </button>
              <button
                class="btn btn-success btn-sm"
                @click="handleApprove"
                :disabled="isProcessing"
              >
                <span v-if="isProcessing" class="loading loading-spinner loading-xs"></span>
                Approuver
              </button>
            </div>
          </div>
        </template>

        <!-- Section info si décidé -->
        <template v-else-if="arbitration.status !== 'PENDING'">
          <div class="divider my-2"></div>
          <div class="space-y-2">
            <label class="text-sm font-semibold">Commentaire de décision</label>
            <p class="text-base-content/80 p-3 bg-base-200 rounded whitespace-pre-wrap">
              {{ arbitration.decisionComment || 'Aucun commentaire' }}
            </p>
            <div v-if="arbitration.validator" class="text-sm text-base-content/60">
              Décision prise par : {{ arbitration.validator?.firstName }} {{ arbitration.validator?.lastName }}<br />
              le {{ formatDate(arbitration.validatedAt) }}
            </div>
          </div>
        </template>
      </div>

      <div class="modal-action">
        <button class="btn" @click="close">Fermer</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="close">close</button>
    </form>
  </dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useCompanyArbitrationStore } from '@/stores/useCompanyArbitrationStore'
import { ARBITRATION_STATUS_LABELS } from '@/constants/arbitrationStatus'
import { formatDate } from '@/utils/dateUtils'
import { useToast } from 'vue-toast-notification'
import HelpButton from '@/components/common/help/HelpButton.vue'
import { useContextualHelp } from '@/composables/useContextualHelp'

interface Props {
  arbitration?: any
  isOpen: boolean
  onArbitrationUpdated?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
})

const emit = defineEmits<{
  close: []
  updated: []
}>()

const boostCoreStore = useBoostCorePermissionStore()
const arbitrationStore = useCompanyArbitrationStore()
const $toast = useToast()

// Aide contextuelle — active uniquement quand la modale est ouverte ; surcharge
// l'aide de la page puis la restaure à la fermeture.
useContextualHelp('arbitration-detail', () => props.isOpen)

const dialogRef = ref<HTMLDialogElement | null>(null)
const decisionComment = ref('')
const isProcessing = ref(false)
const newComment = ref('')
const isAddingComment = ref(false)
const localComment = ref<string | null>(null)
const commentBoxRef = ref<HTMLDivElement | null>(null)

const canValidate = computed(
  () => boostCoreStore.include('ARBITRATION_VALIDATE') || boostCoreStore.include('SUPER_ADMIN'),
)

interface ReasonDetails {
  description?: string
  commercialArgument?: string
}

const reasonObject = computed<ReasonDetails | null>(() => {
  const reason = props.arbitration?.reason
  if (!reason) return null
  if (typeof reason === 'object' && reason !== null) {
    return { description: reason.description, commercialArgument: reason.commercialArgument }
  }
  if (typeof reason === 'string') {
    try {
      const parsed = JSON.parse(reason)
      if (parsed && typeof parsed === 'object') {
        return { description: parsed.description, commercialArgument: parsed.commercialArgument }
      }
    } catch {
      return null
    }
  }
  return null
})

function scrollCommentToBottom() {
  nextTick(() => {
    if (commentBoxRef.value) commentBoxRef.value.scrollTop = commentBoxRef.value.scrollHeight
  })
}

watch(
  () => props.arbitration?.comment,
  (val) => {
    localComment.value = val ?? null
    scrollCommentToBottom()
  },
  { immediate: true },
)

watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      dialogRef.value?.showModal()
    } else {
      dialogRef.value?.close()
      decisionComment.value = ''
      newComment.value = ''
    }
  },
)

async function handleAddComment() {
  if (!newComment.value.trim()) return
  isAddingComment.value = true
  try {
    const updated = await arbitrationStore.addArbitrationComment(props.arbitration.uuid, newComment.value)
    if (updated?.comment !== undefined) localComment.value = updated.comment
    newComment.value = ''
    $toast.success('Commentaire ajouté')
  } catch (error) {
    $toast.error('Erreur lors de l\'ajout du commentaire')
    console.error(error)
  } finally {
    isAddingComment.value = false
  }
}

async function handleApprove() {
  isProcessing.value = true
  try {
    await arbitrationStore.updateArbitrationStatus(props.arbitration.uuid, 'APPROVED', decisionComment.value || undefined)
    $toast.success('Arbitrage approuvé')
    props.onArbitrationUpdated?.()
    emit('updated')
    close()
  } catch (error) {
    $toast.error('Erreur lors de l\'approbation')
    console.error(error)
  } finally {
    isProcessing.value = false
  }
}

async function handleReject() {
  if (!decisionComment.value.trim()) {
    $toast.error('Le commentaire est obligatoire en cas de rejet')
    return
  }
  isProcessing.value = true
  try {
    await arbitrationStore.updateArbitrationStatus(props.arbitration.uuid, 'REJECTED', decisionComment.value)
    $toast.success('Arbitrage refusé — commande annulée')
    props.onArbitrationUpdated?.()
    emit('updated')
    close()
  } catch (error) {
    $toast.error('Erreur lors du rejet')
    console.error(error)
  } finally {
    isProcessing.value = false
  }
}

function close() {
  dialogRef.value?.close()
  decisionComment.value = ''
  emit('close')
}

const arbitration = computed(() => props.arbitration)
</script>
